import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocalization } from '../contexts/LocalizationContext';
import api, { getApiImageUrl } from '../services/api';
import './ModuleReviewScreen.css';

const ModuleReviewScreen = ({ currentPlayer }) => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLocalization();
  
  const [moduleData, setModuleData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingTask, setLoadingTask] = useState(false);

  // Helper function for localization with fallbacks
  const getTranslation = (key, fallback) => {
    const translation = t(key);
    if (translation === key || !translation) {
      console.warn(`Translation missing for key: ${key}, using fallback: ${fallback}`);
      return fallback;
    }
    return translation;
  };

  useEffect(() => {
    if (currentPlayer && moduleId) {
      fetchModuleReview();
    }
  // Re-fetch on language change so the module title follows the selected language
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPlayer, moduleId, language]);

  const fetchModuleReview = async () => {
    if (!currentPlayer) {
      setError('No player selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/api/module/${moduleId}/review`, {
        params: { player_id: currentPlayer.id, language: language }
      });

      const data = response.data;
      setModuleData(data.module);
      setTasks(data.tasks);
    } catch (err) {
      console.error('Error fetching module review:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskClick = async (task) => {
    if (loadingTask) return;
    
    try {
      setLoadingTask(true);
      
      // Fetch task details including full card content
      const response = await api.get(`/api/module/${moduleId}/task/${task.id}/review`, {
        params: { player_id: currentPlayer.id, language: language }
      });

      const taskDetails = response.data;
      console.log('Task details for review:', taskDetails);
      
      // Navigate to ResultScreen with all necessary data
      navigate('/result', {
        state: {
          result: {
            correct: taskDetails.wasCorrect,  // Use actual correct/incorrect status
            resultScreen: {
              type: 'review',
              richFeedback: taskDetails.explanation,
              messageParams: {
                correctAnswer: taskDetails.correctAnswer,
                userAnswer: taskDetails.userAnswer
              }
            },
            correctAnswer: taskDetails.correctAnswer
          },
          moduleName: moduleData?.name || moduleId,
          moduleId: moduleId,
          currentCardId: task.id,
          cardData: {
            content: taskDetails.content,  // Make sure content is nested properly
            id: task.id,
            points: task.points
          },
          userAnswer: taskDetails.userAnswer,
          isReviewMode: true
        }
      });
    } catch (err) {
      console.error('Error loading task for review:', err);
      setError(err.message);
    } finally {
      setLoadingTask(false);
    }
  };

  if (loading) {
    return (
      <div className="module-review-screen">
        <div className="loading">
          <div className="spinner"></div>
          <p>{getTranslation('common.loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="module-review-screen">
        <div className="error">
          <h2>{getTranslation('common.error', 'Error')}</h2>
          <p>{error}</p>
          <button 
            onClick={() => navigate('/play')}
            className="back-button"
          >
            {getTranslation('common.back', 'Back')}
          </button>
        </div>
      </div>
    );
  }

  if (!moduleData || tasks.length === 0) {
    return (
      <div className="module-review-screen">
        <div className="error">
          <h2>{getTranslation('moduleReview.noTasks', 'No tasks found')}</h2>
          <button 
            onClick={() => navigate('/play')}
            className="back-button"
          >
            {getTranslation('common.back', 'Back')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="module-review-screen">
      <div className="review-header">
        <button 
          onClick={() => navigate('/play')}
          className="back-button"
        >
          ← {getTranslation('common.back', 'Back')}
        </button>
        <div className="module-info">
          <h1>{moduleData.name}</h1>
          <p className="module-stats">
            {tasks.filter(t => t.solved).length} / {tasks.length} {getTranslation('moduleReview.tasksSolved', 'tasks solved')}
          </p>
        </div>
      </div>

      <div className="tasks-grid">
        {tasks.map((task, index) => (
          <div 
            key={task.id} 
            className={`task-tile ${task.solved ? 'solved' : 'unsolved'}`}
            onClick={() => handleTaskClick(task)}
          >
            {/* Task number */}
            <div className="task-number">{index + 1}</div>
            
            {/* Status indicator */}
            <div className="task-status">
              {task.solved ? (
                <span className="status-solved">✓</span>
              ) : (
                <span className="status-unsolved">✗</span>
              )}
            </div>

            {/* Task image if available */}
            {task.image && (
              <div className="task-image">
                <img 
                  src={getApiImageUrl(task.image)} 
                  alt={`Task ${index + 1}`}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}

            {/* Task type indicator */}
            <div className="task-type">{task.points} ⭐</div>
          </div>
        ))}
      </div>
      
      {/* Loading overlay for task loading */}
      {loadingTask && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>{getTranslation('common.loading', 'Loading...')}</p>
        </div>
      )}
    </div>
  );
};

export default ModuleReviewScreen;